import { Download, ArrowLeft, Image as ImageIcon } from "lucide-react";

const ADS = [
  {
    src: "/ad/ad-hero-eid-scan.png",
    label: "Hero · EID Scan (1:1)",
    useCase:
      "★ Primary creative — Facebook + Instagram feed. No phone in frame.",
    size: "Square",
  },
  {
    src: "/ad/ad-vertical-hands.png",
    label: "Hands on App (4:5)",
    useCase:
      "★ App-in-action — gloved hand + finger scrolling the real Set Up Collection screen.",
    size: "Vertical 4:5",
  },
  {
    src: "/ad/ad-dna-collection.png",
    label: "DNA Verification (4:5)",
    useCase: "Carousel slide / mobile feed — for DNA-led messaging",
    size: "Vertical 4:5",
  },
  {
    src: "/ad/ad-landscape-wide.png",
    label: "Environmental · Landscape (16:9)",
    useCase: "Desktop feed, header banner, YouTube thumbnail",
    size: "Wide 16:9",
  },
];

const COPY = {
  headline: "Capture. Verify. Send. All from the yard.",
  primary:
    "The hardest part of a yard session isn't the cattle. It's the three hours of paperwork that follow. 🐄\n\n" +
    "Arvo turns one yard session into one clean workflow:\n\n" +
    "📡  Herd Reads — verify every animal ID at the point of capture\n" +
    "🧬  DNA Sampling — lock every sample to the right animal, lab-ready CSV out\n" +
    "🏷️  Stock Verification — on-farm checks against your own NAIT and herd records\n\n" +
    "Capture in the yard. Reports build live as you work. Send them on yourself — to your vet, herd record provider or livestock rep — the moment you walk out the gate.\n\n" +
    "Independent. Provider-neutral. Built for NZ dairy and beef breeding operations.\n\n" +
    "🎁 100 free session credits for everyone on the early-access list.",
  description:
    "Herd Reads · DNA Sampling · Stock Verification · Live session reports · Export & share",
  shortPrimary:
    "Capture, verify and send every yard session from your phone. Herd Reads, DNA sampling and stock verification — all in one tool, built for NZ dairy and beef breeding operations. 100 free credits on the early-access list. 🐄",
  cta: "Sign Up",
  destination: "https://getarvo.co.nz/#download",
};

function CopyBlock({ label, text, mono = false }) {
  return (
    <div className="bg-white border border-[#D5D1C8] rounded-md p-5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-wider text-[#4A4F4A]">
          {label}
        </span>
        <button
          type="button"
          onClick={() => navigator.clipboard?.writeText(text)}
          data-testid={`copy-${label.toLowerCase().replace(/\s/g, "-")}`}
          className="text-xs font-semibold text-[#D96C32] hover:underline"
        >
          Copy
        </button>
      </div>
      <pre
        className={`mt-3 text-sm text-[#1A1D1A] whitespace-pre-wrap leading-relaxed ${
          mono ? "font-mono" : "font-sans"
        }`}
      >
        {text}
      </pre>
    </div>
  );
}

export default function AdPreview() {
  return (
    <main className="min-h-screen bg-[#F9F8F6] py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-baseline justify-between flex-wrap gap-3">
          <div>
            <span className="inline-block text-xs font-bold tracking-[0.2em] uppercase text-[#D96C32]">
              Internal · Facebook Ad — Capture · Verify · Send
            </span>
            <h1 className="mt-2 font-display font-bold text-3xl sm:text-4xl text-[#1A1D1A]">
              Ad creative preview
            </h1>
            <p className="mt-1 text-sm text-[#4A4F4A]">
              Five image formats + paste-ready copy. Right-click → Save image,
              or click Download.
            </p>
          </div>
          <a
            href="/"
            className="text-sm font-semibold text-[#2D4A22] hover:underline inline-flex items-center gap-1"
          >
            <ArrowLeft className="w-4 h-4" /> Back to site
          </a>
        </div>

        {/* Image grid */}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {ADS.map((ad) => (
            <div
              key={ad.src}
              data-testid={`ad-card-${ad.label.toLowerCase().replace(/\W+/g, "-")}`}
              className="bg-white border border-[#D5D1C8] rounded-md overflow-hidden shadow-sm flex flex-col"
            >
              <div className="bg-[#0a0a0a]">
                <img
                  src={ad.src}
                  alt={ad.label}
                  className="w-full h-auto block"
                  loading="lazy"
                />
              </div>
              <div className="p-5 flex-1 flex flex-col">
                <div className="flex items-center gap-2">
                  <ImageIcon className="w-4 h-4 text-[#2D4A22]" />
                  <p className="font-display font-bold text-base text-[#1A1D1A]">
                    {ad.label}
                  </p>
                </div>
                <p className="mt-2 text-xs text-[#4A4F4A]">{ad.useCase}</p>
                <p className="mt-1 text-xs font-mono text-[#4A4F4A]/80">
                  {ad.size}
                </p>
                <a
                  href={ad.src}
                  download
                  className="mt-4 inline-flex items-center justify-center gap-2 bg-[#2D4A22] hover:bg-[#1F3617] text-white text-sm font-bold py-2.5 px-4 rounded-md transition-colors"
                >
                  <Download className="w-4 h-4" /> Download
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* Copy blocks */}
        <h2 className="mt-12 font-display font-bold text-2xl text-[#1A1D1A]">
          Paste-ready ad copy
        </h2>
        <p className="mt-1 text-sm text-[#4A4F4A]">
          Drop these into Facebook Ads Manager. Three-pillar focus: Herd Reads
          · DNA Sampling · Stock Verification.
        </p>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
          <CopyBlock label="Headline" text={COPY.headline} />
          <CopyBlock label="Description" text={COPY.description} />
          <div className="lg:col-span-2">
            <CopyBlock label="Primary text (full)" text={COPY.primary} />
          </div>
          <div className="lg:col-span-2">
            <CopyBlock
              label="Primary text (short — for Instagram / Reels)"
              text={COPY.shortPrimary}
            />
          </div>
          <CopyBlock label="CTA button" text={COPY.cta} />
          <CopyBlock label="Destination URL" text={COPY.destination} mono />
        </div>

        <div className="mt-10 p-5 bg-[#1F3617] text-white rounded-md">
          <p className="font-display font-bold text-lg">Tip for testing</p>
          <p className="mt-2 text-sm text-white/85 leading-relaxed">
            Pair the <strong>EID Scan</strong> hero image with the long
            primary text for cold audiences. Use the <strong>DNA Verification
            </strong> image with the short copy for breeding-focused
            retargeting (DairyNZ / B+LNZ interests). Track which combo wins via
            your <a href="/stats?token=arvo-stats-x9q2k" className="underline font-bold text-[#F2C76A]">visitor stats</a>.
          </p>
        </div>
      </div>
    </main>
  );
}
